import React, { createContext, useContext, useEffect, useId, useRef, useState } from 'react';
import { cx, focusRing, motion } from './classes';

const CloseMenu = createContext(() => {});

/**
 * A button that opens a short list of actions and links below it, such as the account menu.
 * `trigger` renders the button from the props it's given (spread them onto it, ref included).
 * Arrow keys move between items; Escape, Tab or a click outside closes it. Choosing an item closes
 * it and puts focus back on the button, so a dialog it opens returns focus there too.
 * Children are MenuItem, MenuLabel and MenuDivider. onOpenChange hears each open and close.
 */
export const Menu = ({ trigger, align = 'end', onOpenChange, className, children }) => {
  const [open, setOpen] = useState(false);
  const menuId = useId();
  const rootRef = useRef(null);
  const buttonRef = useRef(null);
  const listRef = useRef(null);
  const onOpenChangeRef = useRef(onOpenChange);
  onOpenChangeRef.current = onOpenChange;

  const setOpenAndTell = (next) => {
    setOpen(next);
    onOpenChangeRef.current?.(next);
  };
  const close = () => {
    setOpenAndTell(false);
    buttonRef.current?.focus();
  };

  useEffect(() => {
    if (!open) return undefined;
    listRef.current?.querySelector('[role="menuitem"]')?.focus();
    const onPointerDown = (event) => {
      if (!rootRef.current?.contains(event.target)) {
        setOpen(false);
        onOpenChangeRef.current?.(false);
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [open]);

  const onKeyDown = (event) => {
    const items = [...listRef.current.querySelectorAll('[role="menuitem"]')];
    const index = items.indexOf(document.activeElement);
    const focusAt = (i) => items[(i + items.length) % items.length]?.focus();
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      close();
    } else if (event.key === 'ArrowDown') {
      event.preventDefault();
      focusAt(index + 1);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      focusAt(index - 1);
    } else if (event.key === 'Home') {
      event.preventDefault();
      focusAt(0);
    } else if (event.key === 'End') {
      event.preventDefault();
      focusAt(items.length - 1);
    } else if (event.key === 'Tab') {
      setOpenAndTell(false);
    }
  };

  return (
    <div ref={rootRef} className={cx('relative', className)}>
      {trigger({
        ref: buttonRef,
        'aria-haspopup': 'menu',
        'aria-expanded': open,
        'aria-controls': open ? menuId : undefined,
        onClick: () => setOpenAndTell(!open),
      })}
      {open && (
        <CloseMenu.Provider value={close}>
          {/* A surface card one step up, edged with line: no shadow */}
          <div
            id={menuId}
            ref={listRef}
            role="menu"
            onKeyDown={onKeyDown}
            className={cx(
              'absolute top-full mt-space-2 z-[60] w-max min-w-[240px] max-w-[320px] p-space-2 flex flex-col gap-0.5',
              'rounded-radius-lg bg-surface border border-line font-text text-ink',
              align === 'start' ? 'left-0' : 'right-0'
            )}
          >
            {children}
          </div>
        </CloseMenu.Provider>
      )}
    </div>
  );
};

/**
 * One action or link in a Menu: a button, or a link through `as` (a router Link) with its `to`.
 * `icon` is a Lucide icon shown before the label.
 */
export const MenuItem = ({ as, icon: Icon, onSelect, className, children, ...rest }) => {
  const close = useContext(CloseMenu);
  const Component = as || 'button';
  return (
    <Component
      role="menuitem"
      tabIndex={-1}
      type={Component === 'button' ? 'button' : undefined}
      onClick={() => {
        close();
        onSelect?.();
      }}
      className={cx(
        'w-full min-h-target-min px-space-3 flex items-center gap-space-3 rounded-radius-md bg-transparent border-0',
        'text-left text-body text-ink no-underline transition-colors hover:bg-raised focus:bg-raised',
        motion,
        focusRing,
        className
      )}
      {...rest}
    >
      {Icon && <Icon size={18} strokeWidth={2.5} aria-hidden="true" className="flex-none text-muted" />}
      <span className="min-w-0 truncate">{children}</span>
    </Component>
  );
};

/** Text in a Menu that isn't an action: who's signed in, or what the next items are */
export const MenuLabel = ({ title, children }) => (
  <div className="px-space-3 py-space-2 flex flex-col gap-0.5 min-w-0">
    {title && <span className="text-body-strong text-ink truncate">{title}</span>}
    {children && <span className="text-caption text-muted">{children}</span>}
  </div>
);

/** A hairline between groups of items */
export const MenuDivider = () => <div role="separator" className="my-space-1 h-px bg-line" />;
